/* eslint-disable max-len */
import React from 'react';
import { useSelector } from 'react-redux';
import { VehicleBrandType } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { RootState } from 'app/AppStore';
import { getTechnicalCharacteristicsLabel } from 'components/StockInfo/catalogStockUtils';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { RENATIVE_LABEL, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE } from 'domains/catalog/Catalog.types';
import { getDHReference, getDHReferences, getPrice } from 'domains/references';
import {
  useFetchCrossSellingReferences,
  useFetchDiscontinuedReferences,
  useFetchReuseStocks,
} from 'domains/references/References.requests';
import { SparePartsViewType } from 'domains/user';
import CrossSellingSection from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard/CrossSellingSection/CrossSellingSection';
import ReferenceCardWrapper from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReferenceCardWrapper';
import { getData, hasData } from 'utils';

export interface SearchResultCardsContainerProps {
  referenceNumber: string;
  sparePartsView: SparePartsViewType;
  vehicleBrand: VehicleBrandType;
  seePlateState: [string | undefined, (x: string | undefined) => void];
}

export const SearchResultReferenceContainer = ({
  referenceNumber,
  sparePartsView,
  vehicleBrand,
  seePlateState,
}: SearchResultCardsContainerProps) => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const ref = useSelector((state: RootState) => getDHReference(state, { referenceNumber, vehicleKey }));
  const prices = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const alternativeReferencesNumbers = getData(ref)?.alternativeReferences ?? [];
  const renative =
    getTechnicalCharacteristicsLabel(getData(ref), RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL;
  const alternativeReferences = useSelector((state: RootState) =>
    getDHReferences(state, { vehicleKey, referenceNumbers: alternativeReferencesNumbers }),
  );

  useFetchCrossSellingReferences(alternativeReferencesNumbers, vehicleKey);
  useFetchReuseStocks(alternativeReferencesNumbers);
  const discontinued = useFetchDiscontinuedReferences(getData(ref)?.referenceNumber ?? '???');
  if (!hasData(ref)) {
    return null;
  }

  return (
    <>
      <ReferenceCardWrapper
        referenceNumber={ref.referenceNumber}
        name={ref.name}
        referenceBrand={ref.brand}
        vehicleBrand={vehicleBrand}
        price={getData(prices)}
        sparePartsView={sparePartsView}
        displayPlateLink={true}
        additionalInfo={ref.additionalInformation}
        additionalInfoDisplayStyle={'EXPANDABLE_WITH_PLUS_ICON'}
        linkedReferences={ref.linkedReferences ?? []}
        isApplicableToCurrentVehicle={ref.isApplicableToCurrentVehicle}
        displaySupersessionTooltip={ref.supersessionChainReferences && ref.supersessionChainReferences.length > 0}
        seePlateState={seePlateState}
        genericPart={ref.genericPart}
        isDiscontinued={discontinued.data}
        familyCode={ref.familyCode}
        imageKeys={ref.imageKeys}
        renative={renative}
      />
      <CrossSellingSection mainRef={ref.referenceNumber} crossSellingReferences={ref.crossSelling} type={'catalog'} />
      {alternativeReferences.map((alt, index) => (
        <>
          <ReferenceCardWrapper
            key={`searched-ref-${referenceNumber}-alt-${index}`}
            referenceNumber={alt.referenceNumber}
            name={alt.name}
            referenceBrand={alt.brand}
            vehicleBrand={vehicleBrand}
            price={getData(prices)}
            sparePartsView={sparePartsView}
            displayPlateLink={true}
            additionalInfo={ref.additionalInformation}
            additionalInfoDisplayStyle={'EXPANDABLE_WITH_PLUS_ICON'}
            linkedReferences={alt.linkedReferences ?? []}
            isApplicableToCurrentVehicle={alt.isApplicableToCurrentVehicle}
            displaySupersessionTooltip={alt.supersessionChainReferences && alt.supersessionChainReferences.length > 0}
            seePlateState={seePlateState}
            genericPart={alt.genericPart}
            imageKeys={alt.imageKeys}
            isDiscontinued={discontinued.data}
          />
          <CrossSellingSection
            mainRef={alt.referenceNumber}
            crossSellingReferences={alt.crossSelling}
            type={'catalog'}
          />
        </>
      ))}
    </>
  );
};
